import { MapPin, Target, Globe, Trash2, RotateCcw, AlertCircle, ArchiveRestore, Pencil } from 'lucide-react';
import { SimpleTooltip } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { IconButton } from '@/components/glitch-ui/icon-button';
import { formatReach } from '@/lib/utils';
import type { BusinessLocationRead } from '@/lib/types';
import { metaCampaignDisplayName } from '@/lib/types';
import { CampaignPill } from '@/components/glitch-ui/campaign-pill';

interface LocationRowProps {
  row: BusinessLocationRead;
  /** Display label - caller resolves country/admin name from locationsQueryOptions data. */
  label: string;
  /** Sub-label e.g. "10 km - 53.12, -6.34 - IE". */
  subLabel: string;
  /** Location-type pill label (Country / City / Region / Radius / Worldwide / …). */
  typeLabel?: string;
  /** Estimated audience reach for the Reach column; null/undefined renders "-" (radius targets
   * and geo-targets without a catalog reach). */
  reach?: number | null;
  onDismiss: (uuid: string) => void;
  onResetToAgent?: (uuid: string) => void;
  onResolve?: (row: BusinessLocationRead) => void;
  onRestore?: (uuid: string) => void;
  /** Optional edit handler. When provided, a pencil icon appears in the row. */
  onEdit?: () => void;
  /** Inline error pinned to this row (e.g., restore conflict). */
  errorMessage?: string;
  /** Tier checkbox is unchecked - render row greyed and non-interactive. */
  disabled?: boolean;
}

// #4816: colored pill per location type (Country / City / Region / Radius / Worldwide / etc.).
const getLocationTypePill = (type: string): string => {
  const t = type.toLowerCase();
  if (t === 'country') return 'bg-purple-50 text-purple-700';
  if (t === 'city') return 'bg-green-50 text-green-700';
  if (t === 'radius') return 'bg-amber-50 text-amber-700';
  if (t === 'worldwide' || t === 'global') return 'bg-slate-100 text-slate-700';
  // regions / states / counties / provinces / boroughs / postal codes / etc.
  return 'bg-blue-50 text-blue-700';
};

const wasEdited = (row: BusinessLocationRead): boolean => {
  if (row.source !== 'user') return false;
  const created = new Date(row.created_at).getTime();
  const updated = new Date(row.updated_at).getTime();
  return updated - created > 1000;
};

export function LocationRow({
  row,
  label,
  subLabel,
  typeLabel,
  reach,
  onDismiss,
  onResetToAgent,
  onResolve,
  onRestore,
  onEdit,
  errorMessage,
  disabled = false,
}: LocationRowProps) {
  const isDismissed = row.dismissed_by_user;
  const isOrphan = !isDismissed && !row.is_resolvable;
  // #4: radius targets get a radar icon and Worldwide ("everywhere") a globe, so
  // each reads differently from catalog locations (countries/regions/cities) at a
  // glance; everything else keeps the map pin.
  const typeLc = (typeLabel ?? '').toLowerCase();
  const isRadius = typeLc === 'radius';
  const isWorldwide = typeLc === 'worldwide' || typeLc === 'global';
  const RowIcon = isWorldwide ? Globe : isRadius ? Target : MapPin;
  // #4816: a location targeted by a campaign can't be removed here (it would desync the campaign).
  const linkedCampaigns = row.linked_campaigns ?? [];
  const isCampaignLinked = linkedCampaigns.length > 0;
  // Meta names go through the same friendly-name mapping as the campaign pills below (#6192) —
  // the raw Meta name is an internal "Testing"-style label the user never chose.
  const campaignNames = linkedCampaigns
    .map((c) => (c.platform === 'meta' ? metaCampaignDisplayName(c.name, c.campaign_type ?? '') : c.name))
    .join(', ');
  const showAiSuggested = !isDismissed && row.source === 'agent' && !isOrphan;
  const showResetToAgent = !isDismissed && row.source === 'user' && wasEdited(row) && !!onResetToAgent;
  const editedSuffix =
    !isDismissed && wasEdited(row) ? (
      <>
        {' · '}
        <span className="text-[#290BD9]">edited by you</span>
      </>
    ) : null;

  // ---------- DISMISSED ROW (Show dismissed mode) ----------
  if (isDismissed) {
    return (
      <div
        className="border-b border-gray-100 bg-gray-50 last:border-b-0"
        data-testid="location-row-dismissed"
      >
        <div className="flex items-center gap-3 px-4 py-3">
          <RowIcon className="h-5 w-5 text-gray-300 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-gray-500 line-through truncate">{label}</div>
            <div className="text-xs text-gray-400 truncate">{subLabel}</div>
          </div>
          <span className="rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-medium text-gray-600 flex-shrink-0">
            dismissed
          </span>
          {onRestore && (
            <IconButton variant="primary" aria-label="Restore location" title="Restore" onClick={() => onRestore(row.uuid)}>
              <ArchiveRestore className="h-4 w-4" />
            </IconButton>
          )}
        </div>
        {errorMessage && (
          <div className="border-t border-[#FCA5A5] bg-[#FEE2E2] px-4 py-2 text-xs text-[#B91C1C]">
            {errorMessage}
          </div>
        )}
      </div>
    );
  }

  // ---------- ORPHAN ROW ----------
  if (isOrphan) {
    return (
      <div
        className="flex items-center gap-3 border-l-[3px] border-[#A16207] bg-[#FEF3C7] px-4 py-3"
        data-testid="location-row-orphan"
      >
        <AlertCircle className="h-5 w-5 text-[#A16207] flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-gray-900">
            {label}{' '}
            <span className="text-xs font-normal text-[#A16207]">(needs resolving)</span>
          </div>
          <div className="text-xs text-gray-500 truncate">{subLabel}</div>
        </div>
        <Button
          size="sm"
          className="bg-[#290BD9] hover:bg-[#2309C5] text-white flex-shrink-0"
          onClick={() => onResolve?.(row)}
        >
          Resolve
        </Button>
        <IconButton variant="destructive" aria-label="Remove location" onClick={() => onDismiss(row.uuid)}>
          <Trash2 className="h-4 w-4" />
        </IconButton>
      </div>
    );
  }

  // ---------- ACTIVE ROW ----------
  return (
    <div
      className={`flex items-center gap-3 border-b border-gray-100 px-4 py-3 last:border-b-0 ${
        disabled ? 'opacity-50 pointer-events-none' : ''
      }`}
        data-testid={`location-row-${row.source}`}
        aria-disabled={disabled}
      >
        <RowIcon className="h-5 w-5 text-gray-500 flex-shrink-0" />
        <div className="w-1/3 min-w-0">
          <div className="text-sm font-medium text-gray-900 truncate" title={label}>{label}</div>
          <div className="text-xs text-gray-500 truncate">
            {subLabel}
            {showAiSuggested && !isCampaignLinked && (
              <span title="Suggested by Glitch">{subLabel ? ' · ' : ''}Suggested</span>
            )}
            {editedSuffix}
          </div>
        </div>
        {/* Type column — fixed width; the pill is short so a flex share just wasted space. */}
        <div className="w-28 flex-shrink-0">
          {typeLabel && (
            <span
              className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${getLocationTypePill(typeLabel)}`}
            >
              {typeLabel}
            </span>
          )}
        </div>
        {/* Campaign column — one pill per campaign, vertically stacked (#4816). overflow-hidden +
            min-w-0 keep the column inside its flex track; w-full on the inner wrapper gives the
            pills a definite width to truncate against (without it, max-w-full resolves against an
            indefinite basis and long names — e.g. inherited radius campaigns, #5479 — clip raw and
            push the table too wide). */}
        <div className="flex-1 min-w-0 overflow-hidden">
          {isCampaignLinked ? (
            <div className="flex w-full min-w-0 flex-col items-start gap-1">
              {linkedCampaigns.map((c) =>
                c.platform === 'meta' ? (
                  <CampaignPill
                    key={`meta-${c.slug}`}
                    slug={c.slug}
                    name={metaCampaignDisplayName(c.name, c.campaign_type ?? '')}
                    platform="meta"
                    hash="targeting"
                    title={`Open Meta campaign targeting: ${metaCampaignDisplayName(c.name, c.campaign_type ?? '')}`}
                  />
                ) : (
                  <CampaignPill
                    key={`google-${c.slug}`}
                    slug={c.slug}
                    name={c.name}
                    hash="targeting"
                    title={`Open campaign targeting: ${c.name}`}
                  />
                ),
              )}
            </div>
          ) : (
            <span className="text-sm text-gray-700">-</span>
          )}
        </div>
        {/* Reach column — compact audience estimate; "-" for radius / reach-less targets. pl-8
            opens a clear gap from the Campaign column. */}
        <div className="w-24 flex-shrink-0 pl-8 text-sm tabular-nums text-gray-700">{formatReach(reach)}</div>
        {/* Actions column. Campaign-linked rows show greyed-out edit + delete (disabled) with a
            tooltip — the location is managed by the campaign. */}
        <div className="w-28 flex-shrink-0 flex items-center justify-end gap-0.5">
          {isCampaignLinked ? (
            <SimpleTooltip
              side="top"
              align="end"
              className="max-w-[260px]"
              content={
                <>
                  Used by {linkedCampaigns.length === 1 ? campaignNames : `${linkedCampaigns.length} campaigns`}. To edit
                  or remove this location, change it from {linkedCampaigns.length === 1 ? 'the campaign' : 'those campaigns'}.
                </>
              }
            >
              <span className="flex items-center gap-0.5 cursor-not-allowed" aria-label="Locked by campaign" aria-disabled="true">
                {/* Only show the (greyed) edit affordance for rows that are actually editable —
                    radius rows. Geo rows have no per-row edit, locked or not. */}
                {onEdit && (
                  <span className="flex h-9 w-9 items-center justify-center rounded-md text-gray-300">
                    <Pencil className="h-4 w-4" />
                  </span>
                )}
                <span className="flex h-9 w-9 items-center justify-center rounded-md text-gray-300">
                  <Trash2 className="h-4 w-4" />
                </span>
              </span>
            </SimpleTooltip>
          ) : (
            <>
              {showResetToAgent && (
                <IconButton aria-label="Reset to the suggestion" title="Reset to the suggestion" onClick={() => onResetToAgent?.(row.uuid)}>
                  <RotateCcw className="h-4 w-4" />
                </IconButton>
              )}
              {onEdit && (
                <IconButton aria-label="Edit location" title="Edit" onClick={onEdit}>
                  <Pencil className="h-4 w-4" />
                </IconButton>
              )}
              <IconButton variant="destructive" aria-label="Remove location" onClick={() => onDismiss(row.uuid)}>
                <Trash2 className="h-4 w-4" />
              </IconButton>
            </>
          )}
        </div>
      </div>
  );
}
